// src/components/ReadhitComponent/ReadHitsModals.js
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import exportPDF from './exportPDF.js';
import SequenceAlignment from 'views/SequenceAlignment.js';

// ─────────────────────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────────────────────

function downloadJSON(row) {
  const blob = new Blob([JSON.stringify(row, null, 2)], { type: 'application/json' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `row_details_${row.Run ?? row.qseqid ?? 'export'}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─────────────────────────────────────────────────────────────────────────────
//  TasksTable  – nested table for Tasks objects
// ─────────────────────────────────────────────────────────────────────────────

function TasksTable({ value }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="rh-tasks-table">
        <thead>
          <tr>
            <th>Task</th>
            <th>Status</th>
            <th>Seconds</th>
            <th>Bytes</th>
            <th>GB</th>
            <th>Command</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(value).map(([taskName, td]) => (
            <tr key={taskName}>
              <td>{taskName}</td>
              <td>{td.status  ?? '—'}</td>
              <td>{td.seconds ?? '—'}</td>
              <td>{td.bytes   ?? '—'}</td>
              <td>{td.gb      ?? '—'}</td>
              <td style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {td.command ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

TasksTable.propTypes = {
  value: PropTypes.object.isRequired,
};

// ─────────────────────────────────────────────────────────────────────────────
//  PreviewModal  – full row-detail view
// ─────────────────────────────────────────────────────────────────────────────

export function PreviewModal({ row, onClose }) {
  if (!row) return null;

  const overlayClick = (e) => { if (e.target === e.currentTarget) onClose(); };

  const renderValue = (key, value) => {
    if (key === 'Tasks' && value && typeof value === 'object') {
      return <TasksTable value={value} />;
    }
    if (typeof value === 'object' && value !== null) {
      return <span className="rh-mono-val">{JSON.stringify(value)}</span>;
    }
    return <span className="rh-mono-val">{String(value ?? '—')}</span>;
  };

  return (
    <div className="rh-modal-overlay" onClick={overlayClick}>
      <div className="rh-modal-box rh-preview-modal" role="dialog" aria-modal="true">

        {/* Header */}
        <div className="rh-modal-header">
          <div>
            <div className="rh-modal-title">
              Row Details — {row.qseqid ?? row.Run ?? 'Record'}
            </div>
            {row.sseqid && (
              <div className="rh-modal-subtitle">sseqid: {row.sseqid}</div>
            )}
          </div>
          <button className="rh-modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Body */}
        <div className="rh-modal-body">
          <div className="rh-pv-section">
            <div className="rh-pv-section-title">All Fields</div>
            <table className="rh-kv-table">
              <tbody>
                {Object.entries(row).map(([key, value]) => (
                  <tr key={key}>
                    <td>{key}</td>
                    <td>{renderValue(key, value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="rh-modal-footer">
          <button className="rh-btn-download" onClick={() => downloadJSON(row)}>
            ↓ Download JSON
          </button>
          <button className="rh-btn-close" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

PreviewModal.propTypes = {
  row:     PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

// ─────────────────────────────────────────────────────────────────────────────
//  SeqAlignModal  – sequence alignment visualization
// ─────────────────────────────────────────────────────────────────────────────

export function SeqAlignModal({ row, onClose }) {
  if (!row) return null;

  const overlayClick = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div className="rh-modal-overlay" onClick={overlayClick}>
      <div
        id="modalContentToExport"
        className="rh-modal-box rh-seqalign-modal"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="rh-modal-header">
          <div>
            <div className="rh-modal-title">Sequence Alignment Visualization</div>
            <div className="rh-modal-subtitle">QSEQ &amp; SSEQ</div>
          </div>
          <button className="rh-modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Body */}
        <div className="rh-modal-body" style={{ maxHeight: '65vh', overflow: 'auto' }}>
          <SequenceAlignment alignmentData={row} />
        </div>

        {/* Footer */}
        <div className="rh-modal-footer">
          <button
            className="rh-btn-download"
            onClick={() => exportPDF('modalContentToExport')}
          >
            Export as PDF
          </button>
          <button className="rh-btn-close" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

SeqAlignModal.propTypes = {
  row:     PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

// ─────────────────────────────────────────────────────────────────────────────
//  LoadingModal  – spinner overlay while fetching row details
// ─────────────────────────────────────────────────────────────────────────────

export function LoadingModal() {
  return (
    <div className="rh-modal-overlay">
      <div className="rh-preview-loading">
        <div className="rh-spinner" />
        <span>Loading row details…</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  ErrorModal  – lightweight error display
// ─────────────────────────────────────────────────────────────────────────────

export function ErrorModal({ message, onClose }) {
  if (!message) return null;

  const overlayClick = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div className="rh-modal-overlay" onClick={overlayClick}>
      <div className="rh-modal-box" style={{ width: 'min(480px, 94vw)' }} role="dialog" aria-modal="true">
        <div className="rh-modal-header">
          <span className="rh-modal-title" style={{ color: '#f87171' }}>
            ⚠ Error
          </span>
          <button className="rh-modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="rh-modal-body">
          <div className="rh-error-banner" style={{ margin: 0 }}>
            <div className="rh-error-icon">⚠</div>
            <div>
              <div className="rh-error-title">Something went wrong</div>
              <div className="rh-error-msg">{message}</div>
            </div>
          </div>
        </div>
        <div className="rh-modal-footer">
          <button className="rh-btn-close" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

ErrorModal.propTypes = {
  message: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};